import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { getOrbiSequence, OrbiSequenceElements } from './agent-orb-sequences';

/** Die vier gestalteten Agent-Typen. `twin` entspricht dem Bot-Template
 *  `alpha_dozent` — der Template-Identifier bleibt unangetastet, nur die
 *  Beschriftung heißt im Portal "Twin". */
export type AgentOrbType =
  | 'tutor'
  | 'flexible'
  | 'twin'
  | 'support'
  | 'assist';

/** Zuordnung Bot-Template → Orb. Templates ohne Eintrag bekommen bewusst
 *  keinen Orb; der Aufrufer entscheidet dann über den Fallback. */
export const ORB_BY_TEMPLATE: Record<string, AgentOrbType> = {
  alpha_tutor: 'tutor',
  alpha_flexible: 'flexible',
  alpha_dozent: 'twin',
  alpha_support: 'support',
  alpha_assist: 'assist',
};

/** Augen-Geometrie im viewBox-System des Orbs (20 20 88 88, Kreis r=43
 *  um 64/64). Jeder Typ trägt seinen Charakter über Form und Höhe der
 *  Augenkapseln — das Timing kommt aus dem SCSS. */
interface OrbEyes {
  /** x-Position der linken / rechten Kapsel */
  x1: number;
  x2: number;
  y: number;
  w: number;
  h: number;
  /** Eckenradius; bei w/2 wird die Kapsel zum Oval */
  r: number;
  /** Glanzpunkt im Auge */
  lightY: number;
  lightR: number;
  lightOpacity: number;
  /** Tutor blendet periodisch auf Lachbögen um */
  smile: boolean;
}

const ORB_EYES: Record<AgentOrbType, OrbEyes> = {
  // offen und zugewandt, wird periodisch zum Lachbogen
  tutor: {
    x1: 47, x2: 71, y: 50, w: 10, h: 26, r: 5,
    lightY: 56, lightR: 1.7, lightOpacity: 0.72, smile: true,
  },
  // schmal und konzentriert — dieselbe Rolle wie tutor, aber mit deutlich
  // mehr Stellschrauben, das trägt der Blick mit
  flexible: {
    x1: 48, x2: 71.5, y: 50, w: 9, h: 26, r: 4.5,
    lightY: 56, lightR: 1.6, lightOpacity: 0.7, smile: false,
  },
  // niedriger, halb gesenkte Lider — bedächtig
  twin: {
    x1: 46.5, x2: 70.5, y: 55, w: 11, h: 19, r: 5.5,
    lightY: 59.5, lightR: 1.6, lightOpacity: 0.62, smile: false,
  },
  // weit offen, wach
  support: {
    x1: 47, x2: 71, y: 49, w: 10, h: 27, r: 5,
    lightY: 55, lightR: 1.7, lightOpacity: 0.7, smile: false,
  },
  // schmal, fokussiert
  assist: {
    x1: 48, x2: 71.5, y: 51, w: 8.5, h: 24, r: 4.25,
    lightY: 56.5, lightR: 1.5, lightOpacity: 0.7, smile: false,
  },
};

/** Läuft pro Instanz hoch. Die SVG-`<defs>`-IDs sind dokumentweit gültig —
 *  ohne Suffix würden zwei Orbs auf derselben Seite (Auswahl im Modal plus
 *  Vorschau in der Agent-Karte) auf dieselben Gradienten zugreifen. */
let orbInstanceCounter = 0;

/** Ruhepause zwischen zwei Ausdrücken — bewusst mit Streuung, nicht fix,
 *  damit mehrere Kacheln im Dashboard nicht im Gleichtakt "atmen". Welche
 *  DREI Ausdrücke ein Orb überhaupt zeigt (das eigentliche "Charakter"-
 *  Signal), bestimmt der Aufrufer über `personalitySequences` — siehe
 *  `TutorCardComponent.personalitySequences` für die deterministische
 *  Pro-Agent-Zuteilung. */
const IDLE_REST_MIN_MS = 5000;
const IDLE_REST_MAX_MS = 9500;

@Component({
  selector: 'app-agent-orb',
  templateUrl: './agent-orb.component.html',
  styleUrl: './agent-orb.component.scss',
})
export class AgentOrbComponent implements AfterViewInit, OnDestroy {
  @Input() type: AgentOrbType = 'tutor';

  /** Kantenlänge in px. Entspricht `--alBubbleSize` im Chat-Widget; das
   *  SVG rendert wie dort 8px größer als der Container. */
  @Input() size = 60;

  /** Nur setzen, wenn der Orb allein steht. Steht der Agent-Name daneben,
   *  weglassen — dann meldet sich das SVG als dekorativ ab, statt den
   *  Namen doppelt vorzulesen. */
  @Input() label?: string;

  /** `happy` = der Orb freut sich: er hüpft und die Augen bleiben als
   *  Lachbögen stehen. Gedacht für den Moment, in dem eine Vorlage
   *  bestätigt wird. Im Ruhezustand blendet nur der Tutor periodisch auf
   *  Lachbögen um — hier tun es alle Typen. */
  @Input() mood: 'idle' | 'happy' = 'idle';

  /** IDs aus `agent-orb-sequences.ts` (`ORBI_SEQUENCE_IDS`), die der Orb
   *  in genau dieser Reihenfolge durchläuft, mit Ruhepause dazwischen —
   *  „Normalzustand → Ausdruck → Normalzustand → nächster Ausdruck …",
   *  danach von vorn. Leer (Default) = keine Selbstanimation, unverändertes
   *  Verhalten für Picker/Adoption-Modal. Wird nur einmal in
   *  `ngAfterViewInit` gelesen — ändert sich die Liste zur Laufzeit, läuft
   *  der aktuelle Zyklus zu Ende, bevor Änderungen wirken (kein Use Case
   *  heute: `TutorCardComponent` berechnet sie einmal pro Agent-Instanz). */
  @Input() personalitySequences: string[] = [];

  @ViewChild('actor') private actorRef?: ElementRef<HTMLElement>;
  @ViewChild('shadow') private shadowRef?: ElementRef<HTMLElement>;
  @ViewChild('spinA') private spinARef?: ElementRef<SVGGElement>;
  @ViewChild('spinB') private spinBRef?: ElementRef<SVGGElement>;
  @ViewChild('glow') private glowRef2?: ElementRef<SVGCircleElement>;
  @ViewChild('eyesGroup') private eyesGroupRef?: ElementRef<SVGGElement>;
  @ViewChild('openEyesGroup') private openEyesGroupRef?: ElementRef<SVGGElement>;
  @ViewChild('smileEyesGroup') private smileEyesGroupRef?: ElementRef<SVGGElement>;
  @ViewChild('shine') private shineRef?: ElementRef<SVGGElement>;
  @ViewChild('mouthWhistleClosed') private mouthWhistleClosedRef?: ElementRef<SVGCircleElement>;
  @ViewChild('note0') private note0Ref?: ElementRef<HTMLElement>;
  @ViewChild('note1') private note1Ref?: ElementRef<HTMLElement>;
  @ViewChild('note2') private note2Ref?: ElementRef<HTMLElement>;
  @ViewChild('speedBack') private speedBackElRef?: ElementRef<SVGSVGElement>;
  @ViewChild('speedFront') private speedFrontElRef?: ElementRef<SVGSVGElement>;
  @ViewChild('orbSvg') private orbSvgRef?: ElementRef<SVGSVGElement>;

  private readonly uid = `agent-orb-${++orbInstanceCounter}`;

  /** Erhöht sich bei jedem `ngOnDestroy` — die laufende `await`-Kette der
   *  Ausdrucks-Schleife prüft ihn nach jeder Pause/Animation und bricht
   *  ansonsten sauber ab. Simpler als ein `AbortController` hier, weil die
   *  Schleife ausschließlich `setTimeout` + `Animation.finished` verkettet,
   *  keine verschachtelten Event-Listener. */
  private destroyed = false;

  /** Alle `Animation`-Objekte der gerade laufenden Sequenz — bei
   *  `ngOnDestroy` mitten in einem Ausdruck hart abgebrochen, damit eine
   *  aus dem DOM entfernte Dashboard-Kachel keine Animation weiterlaufen
   *  lässt (z.B. beim Filtern/Scrollen der Agenten-Liste). */
  private activeAnimations: Animation[] = [];

  /** Id der gerade laufenden Sequenz, als `data-sequence`-Attribut am Host
   *  gespiegelt — treibt zwei SCSS-Regeln (agent-orb.component.scss):
   *  `[data-sequence]` pausiert die Ambient-CSS-Animationen (Spin/Scan/
   *  Blink/Tutor-Lachbogen-Wechsel), solange irgendeine Sequenz läuft, und
   *  `[data-sequence="spin-centrifugal"]` zentriert `.agent-orb__actor`s
   *  Rotationsursprung um (statt des Hüpfer-Bodenankers). Lab-Vorbild:
   *  `data-sequence`/`data-performing` in core/orbi.js. */
  activeSequenceId: string | null = null;

  /** Sequenz-Id, die eine umgebende Szene von außen setzt, wenn SIE den Orb
   *  animiert statt der eigenen Ausdrucks-Schleife — heute nur das
   *  Verteil-Modal (`orbi-distribute-dialog`, `split-swarm-distribute`).
   *  Wirkt wie `activeSequenceId`: pausiert die Ambient-Animationen
   *  (Lab-Vorbild: `data-performing` an den Mini-Orbis in
   *  core/mini-orbi-swarm.js). */
  @Input() externalSequence: string | null = null;

  @HostBinding('attr.data-sequence') get dataSequence(): string | null {
    return this.activeSequenceId ?? this.externalSequence;
  }

  @HostBinding('class') get hostClass(): string {
    const happy = this.mood === 'happy' ? ' agent-orb--happy' : '';
    return `agent-orb agent-orb--${this.type}${happy}`;
  }

  get eyes(): OrbEyes {
    return ORB_EYES[this.type];
  }

  /** Lachbögen sitzen unter den Augenkapseln des jeweiligen Typs. */
  get showSmile(): boolean {
    return this.eyes.smile || this.mood === 'happy';
  }

  get smilePathLeft(): string {
    return `M${this.eyes.x1} 68c2.5-6 7.5-6 10 0`;
  }

  get smilePathRight(): string {
    return `M${this.eyes.x2} 68c2.5-6 7.5-6 10 0`;
  }

  get svgSize(): number {
    return this.size + 8;
  }

  // --- instanz-eigene <defs> --------------------------------------------
  get clipId(): string { return `${this.uid}-clip`; }
  get fillId(): string { return `${this.uid}-fill`; }
  get glowId(): string { return `${this.uid}-glow`; }
  get bandAId(): string { return `${this.uid}-band-a`; }
  get bandBId(): string { return `${this.uid}-band-b`; }
  get rimId(): string { return `${this.uid}-rim`; }
  get blurId(): string { return `${this.uid}-blur`; }
  get eyeGlowId(): string { return `${this.uid}-eye-glow`; }
  get speedBackId(): string { return `${this.uid}-speed-back`; }
  get speedFrontId(): string { return `${this.uid}-speed-front`; }

  get clipRef(): string { return `url(#${this.clipId})`; }
  get fillRef(): string { return `url(#${this.fillId})`; }
  get glowRef(): string { return `url(#${this.glowId})`; }
  get bandARef(): string { return `url(#${this.bandAId})`; }
  get bandBRef(): string { return `url(#${this.bandBId})`; }
  get rimRef(): string { return `url(#${this.rimId})`; }
  get blurRef(): string { return `url(#${this.blurId})`; }
  get eyeGlowRef(): string { return `url(#${this.eyeGlowId})`; }
  get speedBackRef(): string { return `url(#${this.speedBackId})`; }
  get speedFrontRef(): string { return `url(#${this.speedFrontId})`; }

  // -----------------------------------------------------------------------
  // Ausdrucks-Schleife
  // -----------------------------------------------------------------------

  ngAfterViewInit(): void {
    if (!this.personalitySequences.length) return;
    // Rein dekorativ — bei reduzierter Bewegung starten wir die Schleife
    // gar nicht erst, statt sie nur zu verkürzen (anders als der Hüpfer
    // bei `mood="happy"`, der eine einzelne bewusste Nutzeraktion
    // bestätigt und deshalb auch verkürzt noch sichtbar bleiben soll).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    void this.runPersonalityLoop();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.activeAnimations.forEach((animation) => animation.cancel());
    this.activeAnimations = [];
  }

  private async runPersonalityLoop(): Promise<void> {
    const sequenceIds = this.personalitySequences;
    let index = 0;
    while (!this.destroyed) {
      await this.restIdle();
      if (this.destroyed) return;

      const sequence = getOrbiSequence(sequenceIds[index]);
      index = (index + 1) % sequenceIds.length;
      if (!sequence) continue; // unbekannte ID — überspringen statt abzubrechen

      const elements = this.collectSequenceElements();
      if (!elements) return; // View schon abgebaut

      // Vor dem ersten Keyframe gesetzt, nicht erst danach: die
      // Ambient-Pause (SCSS `[data-sequence]`) soll bereits im ersten
      // gerenderten Frame der Sequenz greifen, nicht erst einen Tick
      // später.
      this.activeSequenceId = sequence.meta.id;
      const animations = sequence.play(elements, this.animate);
      this.activeAnimations = animations;
      await Promise.allSettled(animations.map((animation) => animation.finished));
      // `fill: 'both'` hält den letzten Frame aktiv, bis die Animation
      // per `cancel()` aus dem Kompositions-Stack genommen wird — sonst
      // bliebe z.B. `.agent-orb__spin-a` für immer auf der letzten Pose
      // der Sequenz eingefroren und die durchgehende CSS-Idle-Rotation
      // darunter (`agentOrbSpinA infinite`) käme nie wieder zum Vorschein.
      // Ohne dieses `cancel()` gäbe es also gar keinen echten Normal-
      // zustand zwischen zwei Ausdrücken, nur ein Standbild.
      animations.forEach((animation) => animation.cancel());
      this.activeAnimations = [];
      this.activeSequenceId = null;
    }
  }

  private restIdle(): Promise<void> {
    const duration = IDLE_REST_MIN_MS + Math.random() * (IDLE_REST_MAX_MS - IDLE_REST_MIN_MS);
    return new Promise((resolve) => setTimeout(resolve, duration));
  }

  /** `OrbiAnimateFn` — dünner Wrapper um `element.animate`, damit
   *  `agent-orb-sequences.ts` keine DOM-Details kennen muss. */
  private animate = (
    element: Element,
    keyframes: Keyframe[],
    options: KeyframeAnimationOptions,
  ): Animation => element.animate(keyframes, options);

  /** Die drei Ebenen, die eine äußere Szene animiert (Verteil-Modal):
   *  Hüpf-Wrapper, Bodenschatten und das SVG selbst. `null`, solange die
   *  View noch nicht steht. */
  sceneElements(): { actor: HTMLElement; shadow: HTMLElement; svg: SVGSVGElement } | null {
    const actor = this.actorRef?.nativeElement;
    const shadow = this.shadowRef?.nativeElement;
    const svg = this.orbSvgRef?.nativeElement;
    return actor && shadow && svg ? { actor, shadow, svg } : null;
  }

  private collectSequenceElements(): OrbiSequenceElements | null {
    const actor = this.actorRef?.nativeElement;
    const shadow = this.shadowRef?.nativeElement;
    const eyes = this.eyesGroupRef?.nativeElement;
    const surfaceA = this.spinARef?.nativeElement;
    const surfaceB = this.spinBRef?.nativeElement;
    const openEyes = this.openEyesGroupRef?.nativeElement;
    const smileEyes = this.smileEyesGroupRef?.nativeElement;
    const glow = this.glowRef2?.nativeElement;
    const shine = this.shineRef?.nativeElement;
    const mouthWhistleClosed = this.mouthWhistleClosedRef?.nativeElement;
    const notes = [this.note0Ref, this.note1Ref, this.note2Ref].map((ref) => ref?.nativeElement);
    const speedBack = this.speedBackElRef?.nativeElement;
    const speedFront = this.speedFrontElRef?.nativeElement;

    if (
      !actor || !shadow || !eyes || !surfaceA || !surfaceB ||
      !openEyes || !smileEyes || !glow || !shine || !mouthWhistleClosed ||
      notes.some((note) => !note) || !speedBack || !speedFront
    ) {
      return null;
    }

    return {
      actor, shadow, eyes, surfaceA, surfaceB, openEyes, smileEyes, glow, shine, mouthWhistleClosed,
      notes: notes as HTMLElement[],
      speedBack,
      speedFront,
      speedPathsBack: Array.from(speedBack.querySelectorAll('path')),
      speedPathsFront: Array.from(speedFront.querySelectorAll('path')),
      svgSize: this.svgSize,
    };
  }
}
